// Store
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import {
  claimFaqQuery,
  fromClaimFaqActions,
  selectFaqAndRisks
} from '@amfam/claim/faq/data-access';
import { env } from '@amfam/environment';
import { FnolRiskModel } from '@amfam/policy/models';
import { AnalyticsActions, AnalyticsFacade, ClaimsAnalytics } from '@amfam/shared/analytics';
import { ClaimsNavigationPaths } from '@amfam/shared/models';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, FaqQuestion } from '@amfam/shared/utility/shared-services';
import { DockingBarService, PageTitleService } from '@amfam/ui-kit';

import { FaqService } from '../../service/faq.service';

@Component({
  selector: 'ds-amfam-claims-faq',
  templateUrl: './claims-faq.component.html',
  standalone: false
})
export class ClaimsFaqComponent implements OnInit, OnDestroy {
  public content: string[];
  public amfamDotComFaqUrl: string;
  public dataLoading: Observable<boolean>;
  public checklistStartOpen: Observable<boolean>;
  public checklist;
  public partnerPropertyContent: Observable<FaqQuestion[]>;
  public partnerAutoContent: Observable<FaqQuestion[]>;
  public isPartner: Observable<boolean>;
  public risks: Array<FnolRiskModel> = new Array<FnolRiskModel>();
  public claimsFNOLEnabled: boolean;
  claimsEnterpriseApp = false;
  impersonating$: Observable<boolean>;
  private stop$: Subject<void> = new Subject<void>();
  constructor(
    private store: Store,
    private dockingBarService: DockingBarService,
    private pageTitle: PageTitleService,
    private analyticsFacade: AnalyticsFacade,
    private route: ActivatedRoute,
    private feature: FeatureFlagService,
    public copyService: CopyService,
    private faqService: FaqService,
    private roleService: ImpersonateRolesService
  ) {
    this.amfamDotComFaqUrl = env.links.amfamDotComClaimsFaqUrl;
  }

  ngOnInit() {
    this.impersonating$ = this.roleService.isAuthorized('file_a_claim');
    this.claimsEnterpriseApp = this.feature.isEnabled('claims_enterprise_app');
    this.dockingBarService.registerHeading('Claims FAQ');
    this.pageTitle.set('Claims FAQ');
    this.analyticsFacade.trackPage(ClaimsAnalytics.pageClaimFAQ);

    this.isPartner = this.store.select(BrandSelectors.selectIsPartner);
    this.isPartner.pipe(take(1)).subscribe(isPartner => {
      if (!isPartner) {
        // eslint-disable-next-line ngrx/prefer-action-creator-in-dispatch
        this.store.dispatch(fromClaimFaqActions.LoadClaimFaq());
        this.setupSitecoreFaq();
      }
    });
    this.claimsFNOLEnabled = this.feature.isEnabled('fnol');

    this.partnerPropertyContent = combineLatest([
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(BrandSelectors.selectPrettyClaimPhoneNum),
      this.store.select(BrandSelectors.selectClaimPhoneNumFullHref),
      this.store.select(BrandSelectors.selectCompany)
    ]).pipe(
      map(([isPartner, prettyPhoneNum, hrefPhoneNum, company]) =>
        this.faqService.retrieveFaqContent('propertyClaims', {
          isPartner: isPartner,
          prettyPhoneNum: prettyPhoneNum,
          hrefPhoneNum: hrefPhoneNum,
          company: company
        })
      )
    );

    this.partnerAutoContent = combineLatest([
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(BrandSelectors.selectPrettyClaimPhoneNum),
      this.store.select(BrandSelectors.selectClaimPhoneNumFullHref),
      this.store.select(BrandSelectors.selectCompany)
    ]).pipe(
      map(([isPartner, prettyPhoneNum, hrefPhoneNum, company]) =>
        this.faqService.retrieveFaqContent('autoClaims', {
          isPartner: isPartner,
          prettyPhoneNum: prettyPhoneNum,
          hrefPhoneNum: hrefPhoneNum,
          company: company
        })
      )
    );

    this.dataLoading = this.store.select(claimFaqQuery.selectClaimsFaqLoading);
    this.checklistStartOpen = this.route.queryParams.pipe(
      take(1),
      map(params => _get(params, 'openAccordion') === 'checklist')
    );
  }

  setupSitecoreFaq(): void {
    this.store
      .select(selectFaqAndRisks)
      .pipe(
        filter(state => !!state.faq),
        take(1)
      )
      .subscribe(state => {
        this.risks = state.risks;
        this.checklist = JSON.parse(state.faq).accidentChecklist;
        this.content = JSON.parse(state.faq).faq.split('|');
        /**
         * AS: The content ends with a '|' so the last element in the array is undesired, so pop it.
         */
        this.content.pop();
      });
  }

  deriveRouting(): void {
    if (this.claimsEnterpriseApp) {
      this.store.dispatch(
        AnalyticsActions.sendAnalytic({
          options: {
            button: { link: 'MyAccount:Claims:FAQAccidentChecklist:FileAClaim' }
          }
        })
      );
      this.store.dispatch(
        fromRouterActions.Go({
          path: [ClaimsNavigationPaths.CLAIM_FNOL_FILE_CLAIM_PATH]
        })
      );
    } else {
      if (_get(this.risks, 'length') === 0)
        this.store.dispatch(
          fromRouterActions.Go({
            path: [ClaimsNavigationPaths.CLAIM_CALLUS_CONTACT_PATH]
          })
        );
      else
        this.store.dispatch(
          fromRouterActions.Go({
            path: [ClaimsNavigationPaths.CLAIM_REPORTFNOL_HOME_PATH]
          })
        );
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
